import React, { useState, useEffect, useRef } from "react";
import { Box, Typography } from "@mui/material";
import { format } from "date-fns";
import { tokens } from "@healthdesk/shared-utils";

// Constants
const CHARS = "0123456789ABCDEFGHIJKLMNOPQRSTUVWXYZ-: ";
const ROW_DELAY = 100;
const DEFAULT_CHAR_STAGGER_MS = 100;
const DEFAULT_FLIP_DURATION_MS = 75;

// Default flap color (queue display)
const DEFAULT_FLAP_COLOR = "secondary.main";

// Hinge + notch (SplitFlapTile-style): crease with background-colored circles = cutout illusion; notch shadows; thin hinge bar; metallic caps (less obtrusive)
const BOARD_BG = tokens.colors.navGlobalManagerBoard.light; // notch circles — flap cutaway illusion
const NOTCH_SIZE_PX = 6; // circular cutout radius (positioned at crease left/right)
const HINGE_CAP_WIDTH_PX = 7;
const HINGE_CAP_HEIGHT_PX = 12;
// Crease: 2px line with gradient (light to dark), subtler
const CREASE_GRADIENT = "linear-gradient(180deg, rgba(255,255,255,0.2), rgba(0,0,0,0.2))";
// Hinge bar: thin line at seam + shadow onto flap surface (mounted hardware feel)
const HINGE_BAR_GRADIENT = "linear-gradient(180deg, rgba(255,255,255,0.15), rgba(0,0,0,0.35))";
const HINGE_BAR_SHADOW = "0 1px 2px rgba(0,0,0,0.35), inset 0 1px 0 rgba(255,255,255,0.2)";
// Hinge cap metallic cylinder with narrow highlight stripe for cylindrical read
const HINGE_CAP_GRADIENT = tokens.splitFlap.hingeCapMetallicGradient;
const HINGE_CAP_BORDER = "1px solid rgba(0,0,0,0.45)";
const HINGE_CAP_SHADOW = "inset 0 1px 0 rgba(255,255,255,0.16), 0 1px 2px rgba(0,0,0,0.40)";
// Notch cutout: stronger inner shadow for deeper cut
const NOTCH_INNER_SHADOW = "inset 0 2px 3px rgba(0,0,0,0.45), inset 0 -1px 1px rgba(255,255,255,0.06)";

/** Default 3D display config (transform + thickness + lighting + timing). Overridable via display3DConfig prop. */
const DISPLAY_3D_DEFAULTS = {
  perspectivePx: 530,
  perspectiveOriginYPercent: 42,
  flapTranslateZPx: 1,
  seamTranslateZPx: 2,
  capTranslateZPx: 3,
  topEasing: "cubic-bezier(0.35, 0.02, 0.7, 0.25)",
  bottomEasing: "cubic-bezier(0.15, 0.8, 0.3, 1)",
  showThicknessEdge: true,
  phaseLighting: true,
  seamHeightPx: 3,
  seamGradient: "linear-gradient(180deg, rgba(255,255,255,0.18) 0%, rgba(60,60,60,0.22) 35%, rgba(0,0,0,0.58) 100%)",
  hingeHoldMs: 10,
  charJitterMs: 2,
  edgeDarkening: true,
  edgeDarkeningStrength: 0.12,
  bottomOvershootDeg: 0,
  capWidthPx: 7,
  capHeightPx: 12,
};

// Flip phases: 'out' = rotate current char to 90deg (edge-on), 'in' = rotate from 90deg to 0deg with new char (so a character is always visible)
const FlipChar = ({
  char = " ",
  flipPhase,
  onAnimationComplete,
  compact,
  backgroundColor = DEFAULT_FLAP_COLOR,
  upperFlapColor,
  lowerFlapColor,
  characterColor = "white",
  fontWeight = "bold",
  flipDurationMs = DEFAULT_FLIP_DURATION_MS,
  use3dFlip = true,
  width: widthProp,
  height: heightProp,
  showCrease = true,
  showHinges = true,
  boardBgColor = BOARD_BG,
  display3DConfig,
  /** When set, bottom flap shows this instead of char (e.g. " " for superscript-only tile). */
  lowerFlapChar,
  /** Optional override for character font size (e.g. for small nav tiles). */
  fontSize: fontSizeProp,
  showCharacterOverlay = true,
  fontFamily = '"Federo", sans-serif',
}) => {
  const d3 = { ...DISPLAY_3D_DEFAULTS, ...display3DConfig };
  const useTwoHalves = upperFlapColor != null && lowerFlapColor != null;
  const useTwoHalfFlip = useTwoHalves && use3dFlip;
  const isFlipping = use3dFlip && (flipPhase === "out" || flipPhase === "in");
  const rotationDeg = flipPhase === "out" ? 90 : 0;

  // For two-half flip: bottom flap 90deg then transition to 0 (or 90 → -4 → 0 when bottomOvershootDeg)
  const [bottomFlapRotation, setBottomFlapRotation] = useState(0);
  useEffect(() => {
    if (flipPhase !== "in") {
      setBottomFlapRotation(0);
      return;
    }
    const overshoot = (d3.bottomOvershootDeg || 0) !== 0;
    setBottomFlapRotation(90);
    if (overshoot) {
      const t1 = setTimeout(() => setBottomFlapRotation(-(d3.bottomOvershootDeg || 0)), Math.round(flipDurationMs * 0.82));
      const t2 = setTimeout(() => setBottomFlapRotation(0), flipDurationMs);
      return () => { clearTimeout(t1); clearTimeout(t2); };
    }
    const id = requestAnimationFrame(() => {
      requestAnimationFrame(() => setBottomFlapRotation(0));
    });
    return () => cancelAnimationFrame(id);
  }, [flipPhase, flipDurationMs, d3.bottomOvershootDeg]);

  useEffect(() => {
    if (isFlipping) {
      const timeout = setTimeout(() => {
        onAnimationComplete?.();
      }, flipDurationMs);
      return () => clearTimeout(timeout);
    }
    if (!use3dFlip && flipPhase) {
      const timeout = setTimeout(() => onAnimationComplete?.(), 0);
      return () => clearTimeout(timeout);
    }
  }, [isFlipping, use3dFlip, flipPhase, onAnimationComplete, flipDurationMs]);

  const effectiveBg = backgroundColor || DEFAULT_FLAP_COLOR;
  const borderTint = useTwoHalves ? upperFlapColor : effectiveBg;
  const borderColor =
    borderTint && borderTint !== DEFAULT_FLAP_COLOR
      ? `${borderTint}99`
      : "rgba(17, 122, 202, 0.3)";

  const width = widthProp ?? (compact ? "1.5rem" : "2rem");
  const height = heightProp ?? (compact ? "2.25rem" : "3rem");
  // Inline style for character so theme/global CSS cannot override (fixes invisible letters in nav/portal).
  // Default size 69cqh = 69% of tile height so letters scale with tile like the original splash.
  const charInline = {
    color: characterColor,
    fontSize: fontSizeProp != null && fontSizeProp !== "" ? fontSizeProp : "69cqh",
    fontFamily,
    fontWeight,
    letterSpacing: "0.01em",
    lineHeight: 1,
    textShadow: "0 1px 1px rgba(0,0,0,0.2)",
    display: "inline-block",
    minHeight: "1em",
  };

  const faceSx = {
    width: "100%",
    height: "100%",
    borderRadius: "4px",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    transition: use3dFlip ? `transform ${flipDurationMs}ms ease-in-out` : "none",
    transformStyle: "preserve-3d",
    transform: use3dFlip ? `rotateX(${rotationDeg}deg)` : "none",
    backfaceVisibility: use3dFlip ? "hidden" : "visible",
    willChange: use3dFlip && flipPhase ? "transform" : "auto",
    border: `1px solid ${borderColor}`,
    boxShadow: "0 1px 2px rgba(0, 0, 0, 0.1)",
    position: "relative",
    overflow: "hidden",
    containerType: "size",
  };

  if (useTwoHalves) {
    faceSx.backgroundColor = "transparent";
  } else {
    faceSx.backgroundColor = effectiveBg;
  }

  // Optional: subtle inset shadow on flap for depth (tile-style)
  if (showHinges) {
    faceSx.boxShadow = "0 1px 2px rgba(0,0,0,0.1), inset 0 1px 0 rgba(255,255,255,0.16), inset 0 -10px 18px rgba(0,0,0,0.12)";
  }

  const notchSize = NOTCH_SIZE_PX;
  const capW = useTwoHalfFlip && d3.capWidthPx != null ? d3.capWidthPx : HINGE_CAP_WIDTH_PX;
  const capH = useTwoHalfFlip && d3.capHeightPx != null ? d3.capHeightPx : HINGE_CAP_HEIGHT_PX;

  // Flap surface lighting: directional gradient (light falloff) instead of solid color
  const topFlapBackground = `linear-gradient(
    180deg,
    rgba(255,255,255,0.10) 0%,
    ${upperFlapColor} 16%,
    ${upperFlapColor} 74%,
    rgba(0,0,0,0.12) 100%
  )`;
  const bottomFlapBackground = `linear-gradient(
    180deg,
    rgba(255,255,255,0.03) 0%,
    ${lowerFlapColor} 18%,
    ${lowerFlapColor} 72%,
    rgba(0,0,0,0.20) 100%
  )`;
  const topEdgeDarkeningOverlay = d3.edgeDarkening && flipPhase === "out" ? "linear-gradient(180deg, rgba(0,0,0,0.00), rgba(0,0,0,0.18))" : "linear-gradient(180deg, rgba(0,0,0,0.00), rgba(0,0,0,0.08))";
  const bottomEdgeDarkeningOverlay = d3.edgeDarkening && flipPhase === "in" ? "linear-gradient(180deg, rgba(0,0,0,0.12), rgba(0,0,0,0.00))" : "none";

  return (
    <Box
      sx={{
        position: "relative",
        width,
        height,
        containerType: "size",
        perspective: use3dFlip ? `${d3.perspectivePx}px` : "none",
        perspectiveOrigin: use3dFlip ? `50% ${d3.perspectiveOriginYPercent}%` : "50% 50%",
        transformStyle: use3dFlip ? "preserve-3d" : "flat",
        margin: 0,
        flexShrink: 0,
      }}
    >
      {/* 1. Flap panel(s) – two-half flip when useTwoHalfFlip, else single rotating face */}
      {useTwoHalfFlip ? (
        <>
          {/* Static underlay top: prevents board background from showing through during flip */}
          <Box
            sx={{
              position: "absolute",
              top: 0,
              left: 0,
              right: 0,
              height: "50%",
              zIndex: 1,
              overflow: "hidden",
              border: `1px solid ${borderColor}`,
              borderBottom: "none",
              borderRadius: "4px 4px 0 0",
              background: topFlapBackground,
              boxShadow: "inset 0 1px 0 rgba(255,255,255,0.06)",
            }}
          >
            {/* Underlay top character half */}
            <Box
              sx={{
                position: "absolute",
                top: 0,
                left: 0,
                right: 0,
                height: "200%",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                transform: "translateY(calc(50% + 1px))",
                zIndex: 1,
                backfaceVisibility: "visible",
              }}
            >
              <span style={charInline}>{char}</span>
            </Box>
          </Box>

          {/* Static underlay bottom: prevents board background from showing through during flip */}
          <Box
            sx={{
              position: "absolute",
              bottom: 0,
              left: 0,
              right: 0,
              height: "50%",
              zIndex: 1,
              overflow: "hidden",
              border: `1px solid ${borderColor}`,
              borderTop: "none",
              borderRadius: "0 0 4px 4px",
              background: bottomFlapBackground,
              boxShadow: "inset 0 -6px 10px rgba(0,0,0,0.12)",
            }}
          >
            {/* Underlay bottom character half */}
            <Box
              sx={{
                position: "absolute",
                bottom: 0,
                left: 0,
                right: 0,
                height: "200%",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                transform: "translateY(calc(-50% - 1px))",
                zIndex: 1,
                backfaceVisibility: "visible",
              }}
            >
              <span
                style={{
                  ...charInline,
                  ...(lowerFlapChar !== undefined &&
                  (lowerFlapChar === " " || lowerFlapChar === "")
                    ? { visibility: "hidden" }
                    : {}),
                }}
              >
                {lowerFlapChar !== undefined
                  ? lowerFlapChar === " " || lowerFlapChar === ""
                    ? "\u200B"
                    : lowerFlapChar
                  : char}
              </span>
            </Box>
          </Box>

          {/* Animated top flap */}
          <Box
            sx={{
              position: "absolute",
              top: 0,
              left: 0,
              right: 0,
              height: "50%",
              marginBottom: "-1px",
              zIndex: 2,
              overflow: "hidden",
              backfaceVisibility: "hidden",
              transformOrigin: "bottom center",
              willChange: "transform, filter",
              transition: `transform ${flipDurationMs}ms ${d3.topEasing}, filter ${flipDurationMs}ms linear, box-shadow ${flipDurationMs}ms linear`,
              transform: `translate3d(0,0,0) translateZ(${d3.flapTranslateZPx}px) rotateX(${
                flipPhase === "out" || flipPhase === "in" ? -90 : 0
              }deg)`,
              filter:
                d3.edgeDarkening && flipPhase === "out"
                  ? `brightness(${1 - (d3.edgeDarkeningStrength ?? 0.12)})`
                  : "brightness(1)",
              border: `1px solid ${borderColor}`,
              borderBottom: "none",
              borderRadius: "4px 4px 0 0",
              background: topFlapBackground,
              boxShadow: showHinges
                ? flipPhase === "out" && d3.phaseLighting
                  ? "0 1px 2px rgba(0,0,0,0.1), inset 0 2px 0 rgba(255,255,255,0.08), inset 0 1px 0 rgba(255,255,255,0.10), inset 0 -10px 14px rgba(0,0,0,0.25)"
                  : "0 1px 2px rgba(0,0,0,0.1), inset 0 2px 0 rgba(255,255,255,0.08), inset 0 1px 0 rgba(255,255,255,0.16), inset 0 -6px 10px rgba(0,0,0,0.08)"
                : "0 1px 2px rgba(0,0,0,0.1), inset 0 2px 0 rgba(255,255,255,0.08)",
              ...(topEdgeDarkeningOverlay !==
                "linear-gradient(180deg, rgba(0,0,0,0.00), rgba(0,0,0,0.08))" && {
                "&::before": {
                  content: '""',
                  position: "absolute",
                  inset: 0,
                  background: topEdgeDarkeningOverlay,
                  pointerEvents: "none",
                  borderRadius: "inherit",
                },
              }),
              ...(d3.showThicknessEdge && {
                "&::after": {
                  content: '""',
                  position: "absolute",
                  left: 0,
                  right: 0,
                  bottom: 0,
                  height: "2px",
                  background:
                    "linear-gradient(180deg, rgba(255,255,255,0.06), rgba(0,0,0,0.30))",
                  transformOrigin: "bottom center",
                  pointerEvents: "none",
                },
              }),
            }}
          >
            <Box
              sx={{
                position: "absolute",
                top: 0,
                left: 0,
                right: 0,
                height: "200%",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                transform: "translateY(calc(50% + 1px))",
                zIndex: 10,
                backfaceVisibility: "visible",
              }}
            >
              <span style={charInline}>{char}</span>
            </Box>
          </Box>

          {/* Animated bottom flap */}
          <Box
            sx={{
              position: "absolute",
              bottom: 0,
              left: 0,
              right: 0,
              height: "50%",
              marginTop: "-1px",
              zIndex: 2,
              overflow: "hidden",
              backfaceVisibility: "hidden",
              transformOrigin: "top center",
              willChange: "transform, filter",
              transition: `transform ${flipDurationMs}ms ${d3.bottomEasing}, filter ${flipDurationMs}ms linear, box-shadow ${flipDurationMs}ms linear`,
              transform: `translate3d(0,0,0) translateZ(${d3.flapTranslateZPx}px) rotateX(${
                flipPhase === "in" ? bottomFlapRotation : 0
              }deg)`,
              filter:
                d3.edgeDarkening && flipPhase === "in"
                  ? `brightness(${1 - (d3.edgeDarkeningStrength ?? 0.12) * 0.85})`
                  : "brightness(1)",
              border: `1px solid ${borderColor}`,
              borderTop: "none",
              borderRadius: "0 0 4px 4px",
              background: bottomFlapBackground,
              boxShadow: showHinges
                ? flipPhase === "in" && d3.phaseLighting
                  ? "0 1px 2px rgba(0,0,0,0.1), inset 0 2px 5px rgba(255,255,255,0.04), inset 0 -12px 16px rgba(0,0,0,0.22)"
                  : "0 1px 2px rgba(0,0,0,0.1), inset 0 1px 0 rgba(255,255,255,0.06), inset 0 -8px 10px rgba(0,0,0,0.14)"
                : "0 1px 2px rgba(0,0,0,0.1)",
              ...(bottomEdgeDarkeningOverlay !== "none" && {
                "&::before": {
                  content: '""',
                  position: "absolute",
                  inset: 0,
                  background: bottomEdgeDarkeningOverlay,
                  pointerEvents: "none",
                  borderRadius: "inherit",
                },
              }),
              ...(d3.showThicknessEdge && {
                "&::after": {
                  content: '""',
                  position: "absolute",
                  left: 0,
                  right: 0,
                  top: 0,
                  height: "2px",
                  background:
                    "linear-gradient(180deg, rgba(255,255,255,0.06), rgba(0,0,0,0.30))",
                  pointerEvents: "none",
                },
              }),
            }}
          >
            <Box
              sx={{
                position: "absolute",
                bottom: 0,
                left: 0,
                right: 0,
                height: "200%",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                transform: "translateY(calc(-50% - 1px))",
                zIndex: 10,
                backfaceVisibility: "visible",
              }}
            >
              <span
                style={{
                  ...charInline,
                  ...(lowerFlapChar !== undefined &&
                  (lowerFlapChar === " " || lowerFlapChar === "")
                    ? { visibility: "hidden" }
                    : {}),
                }}
              >
                {lowerFlapChar !== undefined
                  ? lowerFlapChar === " " || lowerFlapChar === ""
                    ? "\u200B"
                    : lowerFlapChar
                  : char}
              </span>
            </Box>
          </Box>
        </>
      ) : (
        <Box sx={{ ...faceSx, zIndex: 2, ...(showHinges ? { position: "absolute", inset: 0 } : {}) }}>
          {useTwoHalves && (
            <>
              <Box sx={{ position: "absolute", top: 0, left: 0, right: 0, height: "50%", backgroundColor: upperFlapColor, zIndex: 0 }} />
              <Box sx={{ position: "absolute", bottom: 0, left: 0, right: 0, height: "50%", backgroundColor: lowerFlapColor, zIndex: 0 }} />
            </>
          )}
          {/* Split character: top half on top flap, bottom half on bottom flap, aligned to crease */}
          <Box
            sx={{
              position: "absolute",
              top: 0,
              left: 0,
              right: 0,
              height: "50%",
              overflow: "hidden",
              zIndex: 1,
            }}
          >
            <Box
              sx={{
                position: "absolute",
                top: 0,
                left: 0,
                right: 0,
                height: "200%",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                zIndex: 10,
              }}
            >
              <span style={charInline}>{char}</span>
            </Box>
          </Box>
          <Box
            sx={{
              position: "absolute",
              bottom: 0,
              left: 0,
              right: 0,
              height: "50%",
              overflow: "hidden",
              zIndex: 1,
            }}
          >
            <Box
              sx={{
                position: "absolute",
                bottom: 0,
                left: 0,
                right: 0,
                height: "200%",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                zIndex: 10,
              }}
            >
              <span style={{ ...charInline, ...(lowerFlapChar !== undefined && (lowerFlapChar === " " || lowerFlapChar === "") ? { visibility: "hidden" } : {}) }}>
                {lowerFlapChar !== undefined ? (lowerFlapChar === " " || lowerFlapChar === "" ? "\u200B" : lowerFlapChar) : char}
              </span>
            </Box>
          </Box>
        </Box>
      )}
      {/* 2. Crease (seam): gradient line + Z lift + background-colored circles at left/right (fake cutout) */}
      {showCrease && (
        <Box
          sx={{
            position: "absolute",
            left: 0,
            top: "50%",
            transform: `translateY(-${(d3.seamHeightPx || 2) / 2}px) translateZ(${d3.seamTranslateZPx}px)`,
            width: "100%",
            height: `${d3.seamHeightPx}px`,
            background: useTwoHalfFlip ? d3.seamGradient : CREASE_GRADIENT,
            zIndex: 12,
            pointerEvents: "none",
            "&::before": {
              content: '""',
              position: "absolute",
              top: "50%",
              left: notchSize * -0.55,
              width: notchSize,
              height: notchSize,
              background: boardBgColor,
              borderRadius: "50%",
              transform: "translateY(-50%)",
              boxShadow: NOTCH_INNER_SHADOW,
            },
            "&::after": {
              content: '""',
              position: "absolute",
              top: "50%",
              right: notchSize * -0.55,
              width: notchSize,
              height: notchSize,
              background: boardBgColor,
              borderRadius: "50%",
              transform: "translateY(-50%)",
              boxShadow: NOTCH_INNER_SHADOW,
            },
          }}
        />
      )}
      {/* 3. Notch shadows – circular inner shadow so cutout feels recessed */}
      {showHinges && (
        <>
          <Box
            aria-hidden="true"
            sx={{
              position: "absolute",
              top: `calc(50% - ${notchSize / 2}px)`,
              left: notchSize * -0.52,
              width: notchSize,
              height: notchSize,
              borderRadius: "50%",
              zIndex: 5,
              pointerEvents: "none",
              boxShadow: "inset 0 2px 3px rgba(0,0,0,0.45), inset 0 -1px 1px rgba(255,255,255,0.06), 1px 0 2px rgba(0,0,0,0.18)",
            }}
          />
          <Box
            aria-hidden="true"
            sx={{
              position: "absolute",
              top: `calc(50% - ${notchSize / 2}px)`,
              right: notchSize * -0.52,
              width: notchSize,
              height: notchSize,
              borderRadius: "50%",
              zIndex: 5,
              pointerEvents: "none",
              boxShadow: "inset 0 2px 3px rgba(0,0,0,0.45), inset 0 -1px 1px rgba(255,255,255,0.06), -1px 0 2px rgba(0,0,0,0.18)",
            }}
          />
        </>
      )}
      {/* 4. Hinge bar – thin line at seam + shadow onto flap; Z lift when using 3D config */}
      {showHinges && (
        <Box
          aria-hidden="true"
          sx={{
            position: "absolute",
            left: 0,
            top: "50%",
            transform: useTwoHalfFlip ? `translateY(-1px) translateZ(${d3.seamTranslateZPx}px)` : "translateY(-1px)",
            width: "100%",
            height: "2px",
            background: HINGE_BAR_GRADIENT,
            boxShadow: useTwoHalfFlip ? HINGE_BAR_SHADOW : undefined,
            zIndex: 6,
            pointerEvents: "none",
          }}
        />
      )}
      {/* 5. Hinge caps – metallic cylinder; Z lift when using 3D config */}
      {showHinges && (
        <>
          <Box
            aria-hidden="true"
            sx={{
              position: "absolute",
              left: 0,
              top: "50%",
              transform: useTwoHalfFlip ? `translateY(-50%) translateZ(${d3.capTranslateZPx}px)` : "translateY(-50%)",
              width: capW,
              height: capH,
              borderRadius: "4px",
              background: HINGE_CAP_GRADIENT,
              border: HINGE_CAP_BORDER,
              boxShadow: HINGE_CAP_SHADOW,
              zIndex: 7,
              pointerEvents: "none",
            }}
          />
          <Box
            aria-hidden="true"
            sx={{
              position: "absolute",
              right: 0,
              top: "50%",
              transform: useTwoHalfFlip ? `translateY(-50%) translateZ(${d3.capTranslateZPx}px)` : "translateY(-50%)",
              width: capW,
              height: capH,
              borderRadius: "4px",
              background: HINGE_CAP_GRADIENT,
              border: HINGE_CAP_BORDER,
              boxShadow: HINGE_CAP_SHADOW,
              zIndex: 7,
              pointerEvents: "none",
            }}
          />
        </>
      )}
      {/* Character overlay: letter in front of flaps in 3D (translateZ) so it is never obscured */}
      {showCharacterOverlay && (
        <Box
          sx={{
            position: "absolute",
            inset: 0,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 20,
            pointerEvents: "none",
            transformStyle: "preserve-3d",
            transform: use3dFlip ? `translateZ(${Math.max((d3.capTranslateZPx ?? 3) + 2, 6)}px)` : "none",
          }}
        >
          <span style={charInline}>{char}</span>
        </Box>
      )}
    </Box>
  );
};

// SplitFlapRow Component
const SplitFlapRow = ({
  textSegments = [],
  speeds = [],
  rowIndex = 0,
  isClock = false,
  compact = false,
  segmentColors,
  segmentBgColor = "white",
  segmentUpperFlapColor,
  segmentLowerFlapColor,
  characterColor,
  fontWeight,
  charStaggerMs = DEFAULT_CHAR_STAGGER_MS,
  flipDurationMs = DEFAULT_FLIP_DURATION_MS,
  className,
  tileWidth,
  tileHeight,
  onFlapComplete,
  onRowComplete,
  use3dFlip = true,
  showCrease = true,
  showHinges = true,
  boardBgColor,
  display3DConfig,
  /** When set, bottom flap shows this instead of char (e.g. " " for superscript-only). */
  lowerFlapChar,
  /** Optional override for character font size (e.g. for small nav tiles). */
  fontSize: fontSizeProp,
  showCharacterOverlay = true,
  fontFamily = '"Federo", sans-serif',
}) => {
  const hasStartedRef = useRef(false);
  const hasCompletedRef = useRef(false);
  const safetyTimeoutRef = useRef(null);
  const visibilityCheckRef = useRef(null);
  const [segmentStates, setSegmentStates] = useState(() =>
    textSegments.map((segment) =>
      " "
        .repeat(segment.length)
        .split("")
        .map((char) => ({
          currentChar: char,
          flipPhase: null,
          targetChar: char,
          animationIndex: 0,
        }))
    )
  );
  const segmentStatesRef = useRef(segmentStates);
  segmentStatesRef.current = segmentStates;

  /** Force all tiles to final target and fire onRowComplete so animation never gets stuck (e.g. tab throttle, CPU). */
  const forceCompletionRef = useRef(() => {});
  forceCompletionRef.current = () => {
    if (hasCompletedRef.current) return;
    hasCompletedRef.current = true;
    if (safetyTimeoutRef.current) {
      clearTimeout(safetyTimeoutRef.current);
      safetyTimeoutRef.current = null;
    }
    setSegmentStates((prev) =>
      prev.map((segment, segmentIndex) => {
        const targetChars = (textSegments[segmentIndex] ?? "").split("");
        return segment.map((state, charIndex) => ({
          ...state,
          currentChar: targetChars[charIndex] ?? " ",
          targetChar: targetChars[charIndex] ?? " ",
          flipPhase: null,
        }));
      })
    );
    onRowComplete?.();
  };

  useEffect(() => {
    const timeouts = [];
    textSegments.forEach((segment, segmentIndex) => {
      const targetChars = segment.split("");
      const rowDelay = rowIndex * ROW_DELAY;

      targetChars.forEach((targetChar, charIndex) => {
        const row = segmentStatesRef.current[segmentIndex];
        const cell = row && row[charIndex];
        if (cell && cell.currentChar !== targetChar) {
          const id = setTimeout(() => {
            startCharAnimation(segmentIndex, charIndex, targetChar);
          }, rowDelay + charIndex * charStaggerMs);
          timeouts.push(id);
        }
      });
    });
    return () => timeouts.forEach((id) => clearTimeout(id));
  }, [textSegments, rowIndex, charStaggerMs]);

  useEffect(() => {
    hasStartedRef.current = false;
    hasCompletedRef.current = false;
    if (safetyTimeoutRef.current) {
      clearTimeout(safetyTimeoutRef.current);
      safetyTimeoutRef.current = null;
    }
  }, [textSegments]);

  useEffect(() => {
    const allSettled = segmentStates.every((segment, segmentIndex) =>
      segment.every((state, charIndex) => {
        const targetChar = textSegments[segmentIndex]?.[charIndex] ?? " ";
        return state.flipPhase == null && state.currentChar === targetChar;
      })
    );

    if (hasStartedRef.current && !hasCompletedRef.current && allSettled) {
      hasCompletedRef.current = true;
      if (safetyTimeoutRef.current) {
        clearTimeout(safetyTimeoutRef.current);
        safetyTimeoutRef.current = null;
      }
      onRowComplete?.();
    }
  }, [segmentStates, textSegments, onRowComplete]);

  /** Safety: ensure row always reaches final state (handles tab throttle, CPU stalls, dropped timeouts). */
  const totalChars = textSegments.reduce((sum, seg) => sum + (seg?.length ?? 0), 0);
  useEffect(() => {
    if (totalChars === 0) return;
    const safetyMs = Math.max(15000, totalChars * 1200);
    safetyTimeoutRef.current = setTimeout(() => {
      safetyTimeoutRef.current = null;
      forceCompletionRef.current();
    }, safetyMs);
    return () => {
      if (safetyTimeoutRef.current) {
        clearTimeout(safetyTimeoutRef.current);
        safetyTimeoutRef.current = null;
      }
    };
  }, [textSegments, totalChars]);

  /** When tab becomes visible again, if animation is still running give it a short window then force-complete if stuck. */
  useEffect(() => {
    const handleVisibility = () => {
      if (document.visibilityState !== "visible") return;
      if (!hasStartedRef.current || hasCompletedRef.current) return;
      if (visibilityCheckRef.current) clearTimeout(visibilityCheckRef.current);
      visibilityCheckRef.current = setTimeout(() => {
        visibilityCheckRef.current = null;
        if (!hasCompletedRef.current) forceCompletionRef.current();
      }, 3000);
    };
    document.addEventListener("visibilitychange", handleVisibility);
    return () => {
      document.removeEventListener("visibilitychange", handleVisibility);
      if (visibilityCheckRef.current) {
        clearTimeout(visibilityCheckRef.current);
        visibilityCheckRef.current = null;
      }
    };
  }, []);

  const getNextChar = (current, target) => {
    let currentIndex = CHARS.indexOf(current);
    const targetIndex = CHARS.indexOf(target);

    if (currentIndex === -1) currentIndex = 0;
    if (targetIndex === -1) return target;

    let forwardDist =
      (targetIndex - currentIndex + CHARS.length) % CHARS.length;
    let backwardDist =
      (currentIndex - targetIndex + CHARS.length) % CHARS.length;

    return forwardDist <= backwardDist
      ? CHARS[(currentIndex + 1) % CHARS.length]
      : CHARS[(currentIndex - 1 + CHARS.length) % CHARS.length];
  };

  const startCharAnimation = (segmentIndex, charIndex, targetChar) => {
    hasStartedRef.current = true;
    hasCompletedRef.current = false;
    setSegmentStates((prev) => {
      const newStates = prev.map((seg, si) =>
        si === segmentIndex ? [...seg] : seg
      );
      const currentState = newStates[segmentIndex][charIndex];

      newStates[segmentIndex][charIndex] = {
        ...currentState,
        flipPhase: "out",
        targetChar,
        animationIndex: currentState.animationIndex + 1,
      };

      return newStates;
    });
  };

  const hingeHoldMs = display3DConfig?.hingeHoldMs ?? 0;

  const handleCharAnimationComplete = (segmentIndex, charIndex) => {
    const currentState = segmentStates[segmentIndex][charIndex];
    const speed = speeds[segmentIndex] || 35;

    if (currentState.flipPhase === "out") {
      // Hinge hold: brief dwell before bottom flap starts (more mechanical feel)
      const hold = Math.max(0, hingeHoldMs);
      setTimeout(() => {
        setSegmentStates((prev) => {
          const newStates = prev.map((seg, si) =>
            si === segmentIndex ? [...seg] : seg
          );
          const s = newStates[segmentIndex][charIndex];
          const nextChar = getNextChar(s.currentChar, s.targetChar);
          newStates[segmentIndex][charIndex] = {
            ...s,
            currentChar: nextChar,
            flipPhase: "in",
          };
          return newStates;
        });
      }, hold);
    }

    if (currentState.flipPhase === "in") {
      setSegmentStates((prev) => {
        const newStates = prev.map((seg, si) =>
          si === segmentIndex ? [...seg] : seg
        );
        newStates[segmentIndex][charIndex] = {
          ...currentState,
          flipPhase: null,
        };
        return newStates;
      });
      onFlapComplete?.(segmentIndex, charIndex);
      if (currentState.currentChar !== currentState.targetChar) {
        setTimeout(() => {
          startCharAnimation(segmentIndex, charIndex, currentState.targetChar);
        }, speed);
      }
    }
  };

  return (
    <Box
      className={className}
      sx={
        isClock
          ? {
              display: "flex",
              justifyContent: "center",
              width: "100%",
            }
          : {
              display: "grid",
              gridTemplateColumns: compact
                ? "5rem 7rem 9rem"
                : "10rem 9rem 12rem",
              gap: compact ? "1rem" : "5rem",
              minWidth: "100%",
              justifyContent: "center",
            }
      }
    >
      {segmentStates.map((segment, segmentIndex) => (
        <Box
          key={segmentIndex}
          sx={{
            display: "flex",
            gap: "1px",
            bgcolor: segmentBgColor,
            p: 0,
            borderRadius: 0,
          }}
        >
          {segment.map((state, charIndex) => {
            const upperColor = Array.isArray(segmentUpperFlapColor)
              ? segmentUpperFlapColor[segmentIndex]
              : segmentUpperFlapColor;
            const lowerColor = Array.isArray(segmentLowerFlapColor)
              ? segmentLowerFlapColor[segmentIndex]
              : segmentLowerFlapColor;
            const charJitterMs = display3DConfig?.charJitterMs ?? 0;
            const jitter = charJitterMs ? ((charIndex % 3) - 1) * charJitterMs : 0;
            const effectiveFlipMs = Math.max(10, flipDurationMs + jitter);
            return (
              <FlipChar
                key={`${segmentIndex}-${charIndex}-${state.currentChar}-${state.animationIndex}`}
                char={state.currentChar}
                flipPhase={state.flipPhase}
                onAnimationComplete={() =>
                  handleCharAnimationComplete(segmentIndex, charIndex)
                }
                compact={compact}
                backgroundColor={segmentColors?.[segmentIndex]}
                upperFlapColor={upperColor}
                lowerFlapColor={lowerColor}
                characterColor={characterColor}
                fontWeight={fontWeight}
                flipDurationMs={effectiveFlipMs}
                use3dFlip={use3dFlip}
                width={tileWidth}
                height={tileHeight}
                showCrease={showCrease}
                showHinges={showHinges}
                boardBgColor={boardBgColor}
                display3DConfig={display3DConfig}
                lowerFlapChar={lowerFlapChar}
                fontSize={fontSizeProp}
                showCharacterOverlay={showCharacterOverlay}
                fontFamily={fontFamily}
              />
            );
          })}
        </Box>
      ))}
    </Box>
  );
};

// Clock Component
const Clock = ({ SplitFlapRow }) => {
  const [time, setTime] = useState(formatTime(new Date()));

  function formatTime(date) {
    const hours = format(date, "h");
    const paddedHours = hours.length === 1 ? `0${hours}` : hours;
    const minutes = format(date, "mm");
    const period = format(date, "a").charAt(0).toUpperCase();
    return `${paddedHours}:${minutes}${period}`;
  }

  useEffect(() => {
    const interval = setInterval(() => {
      setTime(formatTime(new Date()));
    }, 1000);
    return () => clearInterval(interval);
  }, []);

  return (
    <Box
      sx={{
        display: "flex",
        justifyContent: "center",
        mb: 4,
      }}
    >
      <Box
        sx={{
          display: "flex",
          bgcolor: "white",
          p: 0.5,
          borderRadius: 1,
          width: "auto",
        }}
      >
        <SplitFlapRow
          textSegments={[time]}
          speeds={[35]}
          rowIndex={0}
          isClock={true}
        />
      </Box>
    </Box>
  );
};

// Main QueueDisplay Component
const QueueDisplay = ({
  queue = [],
  compact = false,
  showClock = true,
  showHeader = true,
}) => {
  // If queue is empty, don't render anything
  if (queue.length === 0) {
    return null;
  }

  const formatTime = (timestamp) => {
    const date = new Date(timestamp);
    const hours = date.getHours() % 12 || 12;
    const minutes = date.getMinutes().toString().padStart(2, "0");
    const ampm = date.getHours() >= 12 ? "P" : "A";
    return `${String(hours).padStart(2, "0")}:${minutes}${ampm}`;
  };

  const formatPhone = (phone) => {
    return phone.slice(-4);
  };

  return (
    <Box sx={{ my: compact ? 2 : 3 }}>
      {showClock && <Clock SplitFlapRow={SplitFlapRow} />}

      {showHeader && (
        <Typography
          variant="h5"
          sx={{
            color: "secondary.main",
            fontWeight: "bold",
            textAlign: "center",
            mb: compact ? 3 : 5,
            letterSpacing: "0.1em",
            fontSize: compact ? "1.25rem" : undefined,
          }}
        >
          VIRTUAL QUEUE STATUS
        </Typography>
      )}

      <Box
        sx={{
          mb: compact ? 2 : 3,
          display: "grid",
          gridTemplateColumns: compact ? "5rem 7rem 9rem" : "10rem 9rem 12rem",
          gap: compact ? "1rem" : "5rem",
          px: 0.5,
          width: "100%",
          justifyContent: "center",
        }}
      >
        <Typography
          variant="subtitle2"
          sx={{
            color: "secondary.main",
            fontWeight: "bold",
            letterSpacing: "0.1em",
            fontSize: compact ? "0.75rem" : undefined,
          }}
        >
          {compact ? "ID" : "PATIENT ID"}
        </Typography>
        <Typography
          variant="subtitle2"
          sx={{
            color: "secondary.main",
            fontWeight: "bold",
            letterSpacing: "0.1em",
            fontSize: compact ? "0.75rem" : undefined,
          }}
        >
          PHONE
        </Typography>
        <Typography
          variant="subtitle2"
          sx={{
            color: "secondary.main",
            fontWeight: "bold",
            letterSpacing: "0.1em",
            fontSize: compact ? "0.75rem" : undefined,
          }}
        >
          WAIT TIME
        </Typography>
      </Box>

      {queue.slice(0, 8).map((patient, index) => (
        <Box key={patient.id} sx={{ mb: compact ? 0.5 : 1 }}>
          <SplitFlapRow
            textSegments={[
              String(patient.id).padStart(2, "0"),
              formatPhone(patient.phone),
              formatTime(patient.date),
            ]}
            speeds={[35, 35, 35]}
            rowIndex={index}
            compact={compact}
          />
        </Box>
      ))}
    </Box>
  );
};

export default QueueDisplay;
export { SplitFlapRow };
